import { filenameFromDisposition } from '../lib/download';
import type { SessionStore } from './session';
import type {
  BillingMonth,
  DayRange,
  Invoice,
  Me,
  Merchant,
  MerchantPaymentList,
  MerchantSummary,
  OtpRequestResponse,
  PanelStatus,
  Problem,
  Reconciliation,
  ResearchExport,
  ResearchSummary,
  StaticQr,
  TokenPair,
} from './types';

/** VITE_ZINQ_API without a trailing slash (default http://localhost:3000). */
export function apiBaseUrl(): string {
  const configured = import.meta.env.VITE_ZINQ_API;
  const base = typeof configured === 'string' && configured.trim() ? configured.trim() : 'http://localhost:3000';
  return base.replace(/\/+$/, '');
}

/**
 * A failed API call. `status` 0 = the API could not be reached. `code` is the API's machine-readable
 * code (PANEL_REQUIRED, DATE_INVALID, …) or a panel one (NETWORK, SESSION_EXPIRED). `detail` is the
 * API's human text — logged, never rendered as HTML.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    readonly problem: Problem | null = null,
  ) {
    super(`API ${status}${code ? ` ${code}` : ''}${problem?.detail ? `: ${problem.detail}` : ''}`);
    this.name = 'ApiError';
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export function isApiError(e: unknown, code?: string): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.code === code);
}

/** The panel plan (350 ден./month) is needed for this (402 PANEL_REQUIRED). */
export function isPanelRequired(e: unknown): boolean {
  return e instanceof ApiError && (e.code === 'PANEL_REQUIRED' || e.status === 402);
}

type Query = Record<string, string | undefined>;

interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** Send the access token and refresh it once on 401 (default true). */
  auth?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

function buildUrl(base: string, path: string, query?: Query): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== '') qs.set(k, v);
  const s = qs.toString();
  return `${base}${path}${s ? `?${s}` : ''}`;
}

async function problemOf(res: Response): Promise<Problem | null> {
  try {
    const text = await res.text();
    if (!text) return null;
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Problem) : null;
  } catch {
    return null;
  }
}

async function errorOf(res: Response): Promise<ApiError> {
  const problem = await problemOf(res);
  const code = typeof problem?.code === 'string' ? problem.code : null;
  return new ApiError(res.status, code, problem);
}

function rangeQuery(range: DayRange): Query {
  return 'date' in range ? { date: range.date } : { from: range.from, to: range.to };
}

/**
 * The ZINQ API for the panel: bearer token from the session store, one refresh (POST
 * /auth/refresh) on a 401 and one retry; a refresh the API refuses signs the panel out.
 * Concurrent 401s share one refresh.
 */
export class ApiClient {
  /** The fetch every call uses (also the live event stream's), injectable for tests. */
  readonly fetchImpl: FetchLike;
  private refreshing: Promise<boolean> | null = null;

  constructor(
    readonly baseUrl: string,
    readonly session: SessionStore,
    fetchImpl?: FetchLike,
  ) {
    this.fetchImpl = fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  // ---------- transport ----------

  private async attempt(
    method: string,
    path: string,
    opts: RequestOptions,
    accept: string,
    token: string | null,
  ): Promise<Response> {
    const headers: Record<string, string> = { accept, ...opts.headers };
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers.authorization = `Bearer ${token}`;
    try {
      return await this.fetchImpl(buildUrl(this.baseUrl, path, opts.query), {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: opts.signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new ApiError(0, 'NETWORK');
    }
  }

  private async send(
    method: string,
    path: string,
    opts: RequestOptions,
    accept: string,
  ): Promise<Response> {
    const auth = opts.auth ?? true;
    let res = await this.attempt(method, path, opts, accept, auth ? this.accessToken() : null);
    if (auth && res.status === 401) {
      const refreshed = await this.refresh();
      if (!refreshed) throw new ApiError(401, 'SESSION_EXPIRED');
      res = await this.attempt(method, path, opts, accept, this.accessToken());
      if (res.status === 401) {
        this.session.set(null, 'expired');
        throw new ApiError(401, 'SESSION_EXPIRED');
      }
    }
    if (!res.ok) throw await errorOf(res);
    return res;
  }

  async json<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const res = await this.send(method, path, opts, 'application/json');
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async file(path: string, fallbackName: string, opts: RequestOptions = {}): Promise<DownloadedFile> {
    const res = await this.send('GET', path, opts, 'text/csv, */*');
    const blob = await res.blob();
    return {
      blob,
      filename: filenameFromDisposition(res.headers.get('content-disposition'), fallbackName),
    };
  }

  accessToken(): string | null {
    return this.session.get()?.accessToken ?? null;
  }

  /**
   * Exchange the refresh token for a new pair. false = the API refused it (the session is cleared);
   * a network / server failure throws and keeps the session.
   */
  refresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;
    const current = this.session.get();
    if (!current) return Promise.resolve(false);
    this.refreshing = (async () => {
      try {
        const res = await this.attempt(
          'POST',
          '/auth/refresh',
          { body: { refreshToken: current.refreshToken }, auth: false },
          'application/json',
          null,
        );
        if (!res.ok) {
          if (res.status >= 400 && res.status < 500) {
            // Only the session that failed is cleared (not one signed in meanwhile).
            if (this.session.get()?.refreshToken === current.refreshToken) {
              this.session.set(null, 'expired');
            }
            return false;
          }
          throw await errorOf(res);
        }
        const pair = (await res.json()) as TokenPair;
        this.session.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
        return true;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  // ---------- auth ----------

  requestOtp(phone: string): Promise<OtpRequestResponse> {
    return this.json('POST', '/auth/otp/request', { body: { phone }, auth: false });
  }

  async verifyOtp(phone: string, code: string): Promise<void> {
    const pair = await this.json<TokenPair>('POST', '/auth/otp/verify', {
      body: { phone, code, deviceName: 'ZINQ web panel' },
      auth: false,
    });
    this.session.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
  }

  /**
   * Revokes the refresh token at the API (best effort) and clears the session. POST /auth/logout
   * is authenticated by the refresh token in the body, so it is sent without the access token:
   * a stale access token can never trigger a refresh here, which would rotate the refresh token
   * mid-logout and leave the new one valid at the API.
   */
  async logout(): Promise<void> {
    const current = this.session.get();
    try {
      if (current) {
        await this.json('POST', '/auth/logout', {
          body: { refreshToken: current.refreshToken },
          auth: false,
        });
      }
    } catch {
      // signed out locally either way
    } finally {
      this.session.set(null, 'logout');
    }
  }

  me(): Promise<Me> {
    return this.json('GET', '/auth/me');
  }

  // ---------- merchant ----------

  merchant(): Promise<Merchant> {
    return this.json('GET', '/merchants/me');
  }

  panel(): Promise<PanelStatus> {
    return this.json('GET', '/merchants/me/panel');
  }

  activatePanel(): Promise<PanelStatus | undefined> {
    return this.json('POST', '/merchants/me/panel');
  }

  cancelPanel(): Promise<PanelStatus | undefined> {
    return this.json('DELETE', '/merchants/me/panel');
  }

  summary(range: DayRange, signal?: AbortSignal): Promise<MerchantSummary> {
    return this.json('GET', '/merchants/me/summary', { query: rangeQuery(range), signal });
  }

  payments(range: DayRange, cursor?: string, signal?: AbortSignal): Promise<MerchantPaymentList> {
    return this.json('GET', '/merchants/me/payments', {
      query: { ...rangeQuery(range), cursor },
      signal,
    });
  }

  reconciliation(from: string, to: string, signal?: AbortSignal): Promise<Reconciliation> {
    return this.json('GET', '/merchants/me/reconciliation', { query: { from, to }, signal });
  }

  exportCsv(from: string, to: string, lang: 'mk' | 'en'): Promise<DownloadedFile> {
    return this.file('/merchants/me/export.csv', `zinq-${from}_${to}.csv`, {
      query: { from, to, lang },
    });
  }

  staticQr(): Promise<StaticQr> {
    return this.json('GET', '/merchants/me/static-qr');
  }

  // ---------- billing ----------

  invoice(month?: string, signal?: AbortSignal): Promise<Invoice> {
    return this.json('GET', '/billing/invoice', { query: { month }, signal });
  }

  billingMonths(): Promise<BillingMonth[]> {
    return this.json('GET', '/billing/months');
  }

  // ---------- real-time ----------

  eventsUrl(): string {
    return `${this.baseUrl}/events/stream`;
  }
}

/**
 * ?studyLabel= and ?from= / ?to= (YYYY-MM-DD, Europe/Skopje days of the session start) on the
 * research summary and exports. Rehearsals are under the study label + '-practice'.
 */
export interface ResearchFilter {
  studyLabel?: string;
  from?: string;
  to?: string;
}

/**
 * The research API (results dashboard): public routes guarded by the x-research-key header, no
 * ZINQ sign-in. The key is the research team's export key — sent only to the ZINQ API.
 */
export class ResearchClient {
  private readonly fetchImpl: FetchLike;

  constructor(
    readonly baseUrl: string,
    private readonly key: string,
    fetchImpl?: FetchLike,
  ) {
    this.fetchImpl = fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  private async get(path: string, query: Query, accept: string): Promise<Response> {
    let res: Response;
    try {
      res = await this.fetchImpl(buildUrl(this.baseUrl, path, query), {
        method: 'GET',
        headers: { accept, 'x-research-key': this.key },
      });
    } catch {
      throw new ApiError(0, 'NETWORK');
    }
    if (!res.ok) {
      const err = await errorOf(res);
      if (res.status === 401) throw new ApiError(401, 'RESEARCH_KEY_INVALID', err.problem);
      throw err;
    }
    return res;
  }

  async summary(filter: ResearchFilter = {}): Promise<ResearchSummary> {
    const res = await this.get('/research/summary', { ...filter }, 'application/json');
    return (await res.json()) as ResearchSummary;
  }

  async exportCsv(kind: ResearchExport, filter: ResearchFilter = {}): Promise<DownloadedFile> {
    const res = await this.get(`/research/export/${kind}.csv`, { ...filter }, 'text/csv, */*');
    const fallback = `zinq-research-${filter.studyLabel || 'all'}-${kind}.csv`;
    return {
      blob: await res.blob(),
      filename: filenameFromDisposition(res.headers.get('content-disposition'), fallback),
    };
  }
}
